import { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GameCanvas } from '@/components/GameCanvas';
import { GameHUD } from '@/components/GameHUD';
import { PauseOverlay } from '@/components/PauseOverlay';
import { GameOverScreen } from '@/components/GameOverScreen';
import { useGameStore } from '@/game';
import { audioManager } from '@/game/audio';
import { triggerHaptic, cancelHaptic } from '@/game/haptics';
import {
  trackLooplayView,
  trackPlayByType,
  trackMatch,
  emitGameEvent,
  startLooplayAttempt,
} from '@/looplay';
import { Play, HelpCircle, Bike, Volume2, VolumeX } from 'lucide-react';
type Phase = 'lobby' | 'how-to-play' | 'playing' | 'paused' | 'gameover';
const MATCH_ID = `lane-rider-${Date.now()}`;
export function HomePage() {
  const [phase, setPhase] = useState<Phase>('lobby');
  const [muted, setMuted] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const isPlaying = useGameStore((s) => s.isPlaying);
  const isPaused = useGameStore((s) => s.isPaused);
  const isGameOver = useGameStore((s) => s.isGameOver);
  const score = useGameStore((s) => s.score);
  const highScore = useGameStore((s) => s.highScore);
  const startGame = useGameStore((s) => s.startGame);
  const pauseGame = useGameStore((s) => s.pauseGame);
  const resumeGame = useGameStore((s) => s.resumeGame);
  // Track view once on mount
  useEffect(() => {
    trackLooplayView();
  }, []);
  // Initialize audio lazily on first user gesture (autoplay policy)
  useEffect(() => {
    const onFirstGesture = () => {
      audioManager.init();
      audioManager.resume();
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
    window.addEventListener('pointerdown', onFirstGesture, { once: true });
    window.addEventListener('keydown', onFirstGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onFirstGesture);
      window.removeEventListener('keydown', onFirstGesture);
    };
  }, []);
  // Reactive audio + haptics for store changes (collision, pickup, powerup)
  useEffect(() => {
    const prev = {
      score: useGameStore.getState().score,
      lives: useGameStore.getState().lives,
      speed: useGameStore.getState().speed,
      powerUps: useGameStore.getState().powerUps,
    };
    const unsub = useGameStore.subscribe((state) => {
      // Crash: lives decreased
      if (state.lives < prev.lives) {
        audioManager.playSound('crash');
        triggerHaptic('heavy');
      }
      // Score gained: distinguish pickup (+50) vs powerup (+100 + activation)
      if (state.score > prev.score) {
        const delta = state.score - prev.score;
        if (delta >= 50 && delta < 200) {
          audioManager.playSound('coin');
          triggerHaptic('light');
        } else if (delta >= 200) {
          audioManager.playSound('powerup');
          triggerHaptic('success');
        }
      }
      // Power-up activation (any was off, now on)
      const activated = (['speed', 'fly', 'shield'] as const).find(
        (k) => state.powerUps[k] && !prev.powerUps[k]
      );
      if (activated) {
        audioManager.playSound('powerup');
        triggerHaptic('success');
      }
      // Lifeline: lives went up
      if (state.lives > prev.lives) {
        audioManager.playSound('lifeline');
      }
      prev.score = state.score;
      prev.lives = state.lives;
      prev.speed = state.speed;
      prev.powerUps = state.powerUps;
    });
    return unsub;
  }, []);
  // Engine RPM tracking
  useEffect(() => {
    if (phase !== 'playing') return;
    let raf = 0;
    const tick = () => {
      const state = useGameStore.getState();
      const effectiveSpeed = state.speed * (state.powerUps.speed ? 2 : 1);
      audioManager.setEngineSpeed(effectiveSpeed);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);
  // Sync UI phase with game state + Looplay tracking
  const roundStartedAtRef = useRef<number | null>(null);
  const lastGameOverTrackedRef = useRef<number>(-1);
  useEffect(() => {
    if (isGameOver) {
      const now = Date.now();
      if (lastGameOverTrackedRef.current !== now) {
        lastGameOverTrackedRef.current = now;
        const startedAt = roundStartedAtRef.current;
        const duration = startedAt
          ? Math.max(0, Math.round((now - startedAt) / 1000))
          : 0;
        trackPlayByType({ trackingType: 'round', roundSeconds: duration });
        trackMatch(MATCH_ID, {
          durationSeconds: duration,
          isCompleted: false,
          isWin: false,
        });
        if (score > 0) {
          emitGameEvent('high_score', { value: score });
          if (score >= highScore) {
            emitGameEvent('achievement_unlocked', {
              payload: { kind: 'new_best', score },
            });
          }
        }
        emitGameEvent('level_complete', {
          payload: { reason: 'crash', distance: score },
        });
      }
      audioManager.stopEngine();
      cancelHaptic();
      audioManager.playSound('gameOver');
      triggerHaptic('error');
      roundStartedAtRef.current = null;
      setPhase('gameover');
    } else if (isPlaying && !isPaused) {
      if (phase !== 'playing') {
        setPhase('playing');
      }
    } else if (isPaused) {
      setPhase('paused');
    }
  }, [isPlaying, isPaused, isGameOver, phase, score, highScore]);
  const beginCountdownAndStart = useCallback(() => {
    audioManager.init();
    audioManager.resume();
    setCountdown(3);
    audioManager.playSound('start');
    triggerHaptic('medium');
    startLooplayAttempt();
    let n = 3;
    const id = window.setInterval(() => {
      n -= 1;
      if (n <= 0) {
        window.clearInterval(id);
        setCountdown(null);
        startGame();
        roundStartedAtRef.current = Date.now();
        audioManager.startEngine();
      } else {
        setCountdown(n);
        audioManager.playSound('laneSwitch');
        triggerHaptic('light');
      }
    }, 700);
  }, [startGame]);
  const handlePause = useCallback(() => {
    pauseGame();
    audioManager.stopEngine();
  }, [pauseGame]);
  const handleResume = useCallback(() => {
    resumeGame();
    audioManager.startEngine();
  }, [resumeGame]);
  const handleQuitToLobby = useCallback(() => {
    audioManager.stopEngine();
    useGameStore.getState().resetGame();
    setPhase('lobby');
  }, []);
  const handleRestart = useCallback(() => {
    beginCountdownAndStart();
  }, [beginCountdownAndStart]);
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      audioManager.setMuted(next);
      return next;
    });
  }, []);
  return (
    <div className="h-[100dvh] w-screen overflow-hidden bg-gradient-to-b from-amber-50 to-amber-100 relative select-none touch-none">
      <style>{`
        @keyframes scrollDown {
          from { transform: translateY(-70px); }
          to { transform: translateY(0); }
        }
        @keyframes laneWobble {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
      `}</style>
      {/* Lobby + How-to-play background road animation */}
      {(phase === 'lobby' || phase === 'how-to-play') && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div
            className="absolute inset-0"
            style={{ background: 'repeating-linear-gradient(to bottom, #333 0px, #333 60px, #555 60px, #555 65px)' }}
          />
          <div
            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1.5"
            style={{ background: 'repeating-linear-gradient(to bottom, #fff 0px, #fff 40px, transparent 40px, transparent 80px)' }}
          />
        </div>
      )}
      <div className="relative h-full w-full flex items-center justify-center">
        <AnimatePresence mode="wait">
          {phase === 'lobby' && (
            <LobbyScreen
              key="lobby"
              onStart={beginCountdownAndStart}
              onHowToPlay={() => setPhase('how-to-play')}
              muted={muted}
              onToggleMute={toggleMute}
            />
          )}
          {phase === 'how-to-play' && (
            <HowToPlayScreen
              key="howto"
              onBack={() => setPhase('lobby')}
              onStart={beginCountdownAndStart}
            />
          )}
          {(phase === 'playing' || phase === 'paused') && (
            <motion.div
              key="game"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="relative w-full h-full flex items-center justify-center"
            >
              <GameCanvas />
              <GameHUD onPause={handlePause} />
              <AnimatePresence>
                {phase === 'paused' && (
                  <PauseOverlay
                    onResume={handleResume}
                    onQuit={handleQuitToLobby}
                    muted={muted}
                    onToggleMute={toggleMute}
                    score={score}
                    highScore={highScore}
                  />
                )}
              </AnimatePresence>
              <AnimatePresence>
                {countdown !== null && (
                  <CountdownOverlay key="cd" value={countdown} />
                )}
              </AnimatePresence>
            </motion.div>
          )}
          {phase === 'gameover' && (
            <motion.div
              key="over"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative w-full h-full flex items-center justify-center"
            >
              <GameCanvas />
              <GameOverScreen onRestart={handleRestart} onHome={handleQuitToLobby} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
function CountdownOverlay({ value }: { value: number }) {
  return (
    <motion.div
      key={value}
      initial={{ opacity: 0, scale: 0.3 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 1.5 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none bg-black/40 backdrop-blur-sm"
    >
      <div className="text-[160px] font-black text-amber-400 drop-shadow-[0_4px_24px_rgba(251,191,36,0.6)]">
        {value}
      </div>
    </motion.div>
  );
}
function LobbyScreen({
  onStart,
  onHowToPlay,
  muted,
  onToggleMute,
}: {
  onStart: () => void;
  onHowToPlay: () => void;
  muted: boolean;
  onToggleMute: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-center justify-center px-6 py-8 max-w-md w-full"
    >
      <motion.div
        animate={{
          y: [0, -10, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
        className="bg-amber-500 rounded-full p-5 shadow-2xl mb-4 ring-4 ring-amber-300"
      >
        <Bike className="w-14 h-14 text-white" strokeWidth={2.5} />
      </motion.div>
      <motion.h1
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="text-5xl font-black text-amber-900 text-center tracking-tight leading-none"
      >
        Lane Rider
      </motion.h1>
      <p className="text-amber-800 text-center font-bold mt-2 mb-8 text-base">
        Endless Motorcycle Runner
      </p>
      <div className="flex flex-col gap-4 w-full">
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.03 }}
          onClick={onStart}
          className="bg-amber-500 active:bg-amber-600 text-white font-black text-xl py-5 rounded-3xl shadow-[0_8px_0_rgb(180,83,9)] active:shadow-[0_4px_0_rgb(180,83,9)] active:translate-y-1 transition-all flex items-center justify-center gap-3 min-h-[64px]"
        >
          <Play className="w-7 h-7" strokeWidth={2.5} fill="currentColor" />
          Start Game
        </motion.button>
        <motion.button
          whileTap={{ scale: 0.95 }}
          whileHover={{ scale: 1.03 }}
          onClick={onHowToPlay}
          className="bg-amber-900 active:bg-stone-900 text-amber-50 font-black text-lg py-4 rounded-3xl shadow-[0_6px_0_rgb(28,25,23)] active:shadow-[0_3px_0_rgb(28,25,23)] active:translate-y-1 transition-all flex items-center justify-center gap-3 min-h-[56px]"
        >
          <HelpCircle className="w-6 h-6" strokeWidth={2.5} />
          How to Play
        </motion.button>
      </div>
      <div className="mt-8 text-amber-800/80 text-xs text-center font-semibold max-w-xs">
        Dodge potholes, grab power-ups, ride as far as you can.
      </div>
      <button
        onClick={onToggleMute}
        className="absolute top-4 right-4 bg-amber-900/10 hover:bg-amber-900/20 active:bg-amber-900/30 text-amber-900 rounded-full p-3 min-w-[48px] min-h-[48px] flex items-center justify-center transition-colors"
        aria-label={muted ? 'Unmute sound' : 'Mute sound'}
      >
        {muted ? (
          <VolumeX className="w-6 h-6" strokeWidth={2.5} />
        ) : (
          <Volume2 className="w-6 h-6" strokeWidth={2.5} />
        )}
      </button>
    </motion.div>
  );
}
function HowToPlayScreen({
  onBack,
  onStart,
}: {
  onBack: () => void;
  onStart: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.4 }}
      className="flex flex-col items-stretch justify-center px-5 py-6 max-w-md w-full max-h-[100dvh] overflow-y-auto"
    >
      <div className="bg-amber-50/95 backdrop-blur-md rounded-3xl p-6 shadow-2xl border-4 border-amber-200">
        <h2 className="text-3xl font-black text-amber-900 text-center mb-4">
          How to Play
        </h2>
        <section className="space-y-4">
          <Block title="🎯 Objective">
            Ride as far as you can. Dodge potholes, barriers, and cones.
            Grab power-ups and survive as long as possible.
          </Block>
          <Block title="🕹️ Controls">
            <ul className="space-y-1 text-amber-900">
              <li>• Swipe left / right on mobile</li>
              <li>• Arrow keys or A / D on desktop</li>
              <li>• Tap the pause button anytime</li>
            </ul>
          </Block>
          <Block title="⚡ Power-ups">
            <ul className="space-y-1 text-amber-900">
              <li>
                <span className="font-black">Speed</span> — temporary 2× boost
              </li>
              <li>
                <span className="font-black">Fly</span> — soar over obstacles
              </li>
              <li>
                <span className="font-black">Shield</span> — absorb one hit
              </li>
              <li>
                <span className="font-black">Star</span> — bonus points
              </li>
            </ul>
          </Block>
          <Block title="💡 Tips">
            Plan lane changes early, chain power-ups for huge scores, and
            use Fly mode to clear tight clusters of obstacles.
          </Block>
        </section>
        <div className="flex flex-col gap-3 mt-6">
          <button
            onClick={onStart}
            className="bg-amber-500 active:bg-amber-600 text-white font-black text-lg py-4 rounded-2xl shadow-lg flex items-center justify-center gap-2 min-h-[56px] transition-colors"
          >
            <Play className="w-6 h-6" strokeWidth={2.5} fill="currentColor" />
            Start Game
          </button>
          <button
            onClick={onBack}
            className="bg-amber-900 active:bg-stone-900 text-amber-50 font-bold text-base py-3 rounded-2xl shadow min-h-[48px] transition-colors"
          >
            Back to Lobby
          </button>
        </div>
      </div>
    </motion.div>
  );
}
function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white/60 rounded-2xl p-3">
      <h3 className="font-black text-amber-900 mb-1 text-sm uppercase tracking-wider">
        {title}
      </h3>
      <div className="text-amber-800 text-sm font-semibold leading-snug">
        {children}
      </div>
    </div>
  );
}